import { QuoteFeedExtended } from './feed';
import {
  DetailedQuote,
  ShortInterestParams,
  ShortInterestsDataSet,
  Logo,
  SymbolType,
  DelayedQuote,
  TickerDetail,
  GetQuotesLogoParams,
  PeersResponse,
} from './entities';
import { StockSymbol } from '@benzinga/session';

interface CachedDelayedQuote {
  cachedAt: number;
  quote: DelayedQuote;
}

function getLogoSignedUrlExpiryUnixSec(logo: Logo): number | null {
  const files = logo?.files;
  if (!files || typeof files !== 'object') {
    return null;
  }
  let minExp: number | null = null;
  for (const url of Object.values(files)) {
    if (typeof url !== 'string' || !url) {
      continue;
    }
    const m = url.match(/(?:[?&])x-bz-exp=(\d+)/);
    if (!m) {
      continue;
    }
    const n = parseInt(m[1], 10);
    if (Number.isNaN(n)) {
      continue;
    }
    minExp = minExp === null ? n : Math.min(minExp, n);
  }
  return minExp;
}

export class QuoteStore {
  private static readonly DELAYED_QUOTE_TTL_MS = 60000;

  private quoteFeeds = new Map<SymbolType, QuoteFeedExtended>();
  private activeQuoteFeeds = new Set<QuoteFeedExtended>();
  private detailedQuotes = new Map<SymbolType, DetailedQuote>();
  private delayedQuotes = new Map<SymbolType, CachedDelayedQuote>();
  private quotesLogos = new Map<string | SymbolType, Logo>();
  private tickerDetails = new Map<StockSymbol, TickerDetail>();
  private shortInterestQuotes = new WeakMap<ShortInterestParams, ShortInterestsDataSet>();
  private peers = new Map<StockSymbol, PeersResponse>();

  public addQuoteFeed = (quoteFeed: QuoteFeedExtended): QuoteFeedExtended => {
    this.quoteFeeds.set(quoteFeed.getSecurities(), quoteFeed);
    return quoteFeed;
  };

  public removeQuoteFeed = (symbol: SymbolType): void => {
    this.quoteFeeds.delete(symbol);
  };

  public addActiveQuoteFeed = (quoteFeed: QuoteFeedExtended): QuoteFeedExtended => {
    this.activeQuoteFeeds.add(quoteFeed);
    return quoteFeed;
  };

  public removeActiveQuoteFeed = (quoteFeed: QuoteFeedExtended): void => {
    this.activeQuoteFeeds.delete(quoteFeed);
  };

  public getQuoteFeeds = (): QuoteFeedExtended[] => {
    return Array.from(this.quoteFeeds.values());
  };

  public getActiveQuoteFeeds = (): QuoteFeedExtended[] => {
    return Array.from(this.activeQuoteFeeds.values());
  };

  public hasQuoteFeed = (symbol: SymbolType): boolean => {
    return this.quoteFeeds.has(symbol);
  };

  public getQuoteFeed = (symbol: SymbolType): QuoteFeedExtended | undefined => {
    return this.quoteFeeds.get(symbol);
  };

  public getDetailedQuotes = (symbol: SymbolType): DetailedQuote | undefined => {
    return this.detailedQuotes.get(symbol);
  };

  public setDetailedQuotes = (symbol: SymbolType, quote: DetailedQuote): void => {
    this.detailedQuotes.set(symbol, quote);
  };

  public getDelayedQuotes = (): DelayedQuote[] => {
    const freshQuotes: DelayedQuote[] = [];
    const now = Date.now();

    this.delayedQuotes.forEach((cached, symbol) => {
      if (now - cached.cachedAt < QuoteStore.DELAYED_QUOTE_TTL_MS) {
        freshQuotes.push(cached.quote);
      } else {
        this.delayedQuotes.delete(symbol);
      }
    });

    return freshQuotes;
  };

  public getDelayedQuote = (symbol: SymbolType): DelayedQuote | undefined => {
    const cached = this.delayedQuotes.get(symbol);
    if (!cached) {
      return undefined;
    }

    if (Date.now() - cached.cachedAt > QuoteStore.DELAYED_QUOTE_TTL_MS) {
      this.delayedQuotes.delete(symbol);
      return undefined;
    }

    return cached.quote;
  };

  public setDelayedQuotes = (symbol: SymbolType, quote: DelayedQuote): void => {
    this.delayedQuotes.set(symbol, { cachedAt: Date.now(), quote });
  };

  public shouldFetchDelayedQuotes = (symbols: SymbolType[]): SymbolType[] => {
    const now = Date.now();
    return symbols.filter(symbol => {
      const cached = this.delayedQuotes.get(symbol);
      return !cached || now - cached.cachedAt > QuoteStore.DELAYED_QUOTE_TTL_MS;
    });
  };

  public addQuotesLogos = (symbol: SymbolType, params: GetQuotesLogoParams | undefined, logo: Logo): void => {
    if (params === undefined) {
      this.quotesLogos.set(symbol, logo);
    } else {
      this.quotesLogos.set(JSON.stringify({ ...params, symbol }), logo);
    }
  };

  public getQuotesLogos = (symbol: SymbolType, params: GetQuotesLogoParams | undefined): Logo | undefined => {
    const logo =
      params === undefined ? this.quotesLogos.get(symbol) : this.quotesLogos.get(JSON.stringify({ ...params, symbol }));
    if (!logo) {
      return undefined;
    }
    const urlExpSec = getLogoSignedUrlExpiryUnixSec(logo);
    const nowSec = Math.floor(Date.now() / 1000);
    if (urlExpSec !== null && nowSec >= urlExpSec) {
      if (params === undefined) {
        this.quotesLogos.delete(symbol);
      } else {
        this.quotesLogos.delete(JSON.stringify({ ...params, symbol }));
      }
      return undefined;
    }
    return logo;
  };

  public getShortInterest = (params: ShortInterestParams): ShortInterestsDataSet | undefined => {
    return this.shortInterestQuotes.get(params);
  };

  public setShortInterest = (params: ShortInterestParams, quotes: ShortInterestsDataSet): void => {
    this.shortInterestQuotes.set(params, quotes);
  };

  public getTickerDetails = (symbol: StockSymbol): TickerDetail | undefined => {
    return this.tickerDetails.get(symbol);
  };

  public setTickerDetails = (details: TickerDetail): void => {
    this.tickerDetails.set(details.symbol, details);
  };

  public getPeers = (symbol: StockSymbol): PeersResponse | undefined => {
    return this.peers.get(symbol);
  };

  public setPeers = (symbol: StockSymbol, peersResponse: PeersResponse): void => {
    this.peers.set(symbol, peersResponse);
  };
}
